import type { Session, AccountSwitcherConfig, SetSessionPayload } from './types';
import { MESSAGE_TYPES, type PostMessageData } from './message-protocol';
import { ACTIONS } from './actions';
import { PROTOCOL_VERSION, generateRequestId } from './message-utils';

interface PendingRequest {
  resolve: (value: any) => void;
  reject: (error: Error) => void;
  timeout: ReturnType<typeof setTimeout>;
}

/**
 * PostMessage client for communicating with the auth service iframe
 */
export class PostMessageClient {
  private iframe: HTMLIFrameElement | null = null;
  private iframeOrigin: string;
  private isConnected = false;
  private eventHandlers = new Map<string, Set<Function>>();
  private pendingRequests = new Map<string, PendingRequest>();
  private debug: boolean;
  private serviceApiKey: string | undefined;

  constructor(config: AccountSwitcherConfig) {
    // Use config for potential future enhancements
    const { authServiceUrl, debug: debugMode, serviceApiKey } = config;

    this.iframeOrigin = authServiceUrl
      ? new URL(authServiceUrl).origin
      : '';
    this.debug = debugMode || false;
    this.serviceApiKey = serviceApiKey;
    this.handleMessage = this.handleMessage.bind(this);

    if (typeof window !== 'undefined') {
      window.addEventListener('message', this.handleMessage);
    }
  }

  /**
   * Set the iframe reference
   */
  setIframe(iframe: HTMLIFrameElement, origin: string) {
    this.iframe = iframe;
    this.iframeOrigin = origin;

    if (this.debug) {
      console.log('[AccountSwitcher] Iframe set:', { origin });
    }
  }

  /**
   * Handle incoming PostMessage events
   */
  private handleMessage(event: MessageEvent) {
    // Verify origin if we have one set
    if (this.iframeOrigin && event.origin !== this.iframeOrigin) {
      return;
    }

    const message = event.data as PostMessageData;
    if (!message || !message.type) {
      return;
    }

    if (this.debug) {
      console.log('[AccountSwitcher] Received message:', message);
    }

    // Handle pending request responses
    if (message.requestId && this.pendingRequests.has(message.requestId)) {
      const pending = this.pendingRequests.get(message.requestId)!;
      clearTimeout(pending.timeout);
      this.pendingRequests.delete(message.requestId);

      if (message.type === MESSAGE_TYPES.ERROR) {
        pending.reject(new Error(message.payload?.message || 'Unknown error'));
      } else {
        pending.resolve(message.payload);
      }
      return;
    }

    // Handle connection handshake
    if (message.type === MESSAGE_TYPES.READY) {
      this.handleReady();
      return;
    }

    if (message.type === MESSAGE_TYPES.HANDSHAKE_ACK) {
      this.isConnected = true;
      if (this.debug) {
        console.log('[AccountSwitcher] Connection established', { isConnected: this.isConnected });
      }
      return;
    }

    // Emit events to registered handlers
    // Pass the full message data including requestId for dialog handlers
    this.emit(message.type, { requestId: message.requestId, payload: message.payload });
  }

  /**
   * Handle READY message from iframe
   */
  private handleReady() {
    if (this.debug) {
      console.log('[AccountSwitcher] Iframe ready, sending handshake');
    }

    // Send handshake with service API key and requestId for proper protocol format
    const requestId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const payload = {
      apiKey: this.serviceApiKey,
    };

    this.sendMessage(MESSAGE_TYPES.HANDSHAKE, payload, requestId);
  }

  /**
   * Register an event handler
   */
  on(eventType: string, handler: Function) {
    if (!this.eventHandlers.has(eventType)) {
      this.eventHandlers.set(eventType, new Set());
    }
    this.eventHandlers.get(eventType)!.add(handler);
  }

  /**
   * Unregister an event handler
   */
  off(eventType: string, handler: Function) {
    const handlers = this.eventHandlers.get(eventType);
    if (handlers) {
      handlers.delete(handler);
    }
  }

  /**
   * Emit an event to all registered handlers
   */
  private emit(eventType: string, payload: any) {
    const handlers = this.eventHandlers.get(eventType);
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          handler(payload);
        } catch (error) {
          console.error('[AccountSwitcher] Error in event handler:', error);
        }
      });
    }
  }

  /**
   * Send a message to the iframe
   */
  private sendMessage(type: string, payload?: any, requestId?: string) {
    if (!this.iframe?.contentWindow) {
      if (this.debug) {
        console.warn('[AccountSwitcher] Cannot send message: iframe not ready');
      }
      return;
    }

    const message: PostMessageData = {
      type,
      payload,
      // The auth-service iframe bridge rejects any message without a string
      // requestId (see isValidMessage), so always carry one — generating a
      // fresh id for fire-and-forget sends that don't supply their own.
      requestId: requestId || generateRequestId(),
      timestamp: Date.now(),
      version: PROTOCOL_VERSION,
    };

    if (this.debug) {
      console.log('[AccountSwitcher] Sending message:', message);
    }

    this.iframe.contentWindow.postMessage(message, this.iframeOrigin);
  }

  /**
   * Send a request and wait for response
   */
  private async request(type: string, payload?: any, timeout = 30000): Promise<any> {
    return new Promise((resolve, reject) => {
      const requestId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      const timeoutHandle = setTimeout(() => {
        this.pendingRequests.delete(requestId);
        reject(new Error(`Request timeout: ${type}`));
      }, timeout);

      this.pendingRequests.set(requestId, {
        resolve,
        reject,
        timeout: timeoutHandle,
      });

      this.sendMessage(type, payload, requestId);
    });
  }

  /**
   * Switch to a different account
   */
  async switchAccount(userUuid: string): Promise<void> {
    await this.request(MESSAGE_TYPES.ACTION_REQUEST, {
      action: ACTIONS.SWITCH_ACCOUNT,
      params: { user_uuid: userUuid },
    });
  }

  /**
   * Add a new account
   */
  async addAccount(): Promise<void> {
    await this.request(MESSAGE_TYPES.ACTION_REQUEST, {
      action: ACTIONS.ADD_ACCOUNT,
      params: {},
    });
  }

  /**
   * Manage account (navigate to account management page)
   */
  async manageAccount(): Promise<void> {
    await this.request(MESSAGE_TYPES.ACTION_REQUEST, {
      action: ACTIONS.MANAGE_ACCOUNT,
      params: {},
    });
  }

  /**
   * Update avatar (navigate to avatar update page)
   */
  async updateAvatar(): Promise<void> {
    await this.request(MESSAGE_TYPES.ACTION_REQUEST, {
      action: ACTIONS.UPDATE_AVATAR,
      params: {},
    });
  }

  /**
   * Logout a specific account
   */
  async logoutAccount(userUuid: string): Promise<void> {
    await this.request(MESSAGE_TYPES.ACTION_REQUEST, {
      action: ACTIONS.LOGOUT_ACCOUNT,
      params: { user_uuid: userUuid },
    });
  }

  /**
   * Logout all accounts
   */
  async logoutAll(): Promise<void> {
    await this.request(MESSAGE_TYPES.ACTION_REQUEST, {
      action: ACTIONS.LOGOUT_ALL,
      params: {},
    });
  }

  /**
   * Get current session state
   */
  async getSession(): Promise<Session> {
    return await this.request(MESSAGE_TYPES.ACTION_REQUEST, {
      action: ACTIONS.GET_ACCOUNTS,
      params: {},
    });
  }

  /**
   * Set session from external login (e.g., host application's login flow)
   */
  async setSession(sessionData: SetSessionPayload): Promise<void> {
    return this.request(MESSAGE_TYPES.SET_SESSION, sessionData);
  }

  /**
   * Send dialog response
   */
  sendDialogResponse(type: string, requestId: string, payload: any) {
    this.sendMessage(type, payload, requestId);
  }

  /**
   * Public: post a fully-enveloped, fire-and-forget message at the iframe.
   *
   * Unlike `request()`, this does not await a response. A requestId, timestamp
   * and protocol version are attached automatically so the auth-service iframe's
   * isValidMessage() accepts the message. Used by the cross-product handoff
   * (sharing/multiAccountAutoAdd) via the provider context's `sendMessage`.
   */
  send(type: string, payload?: any): void {
    this.sendMessage(type, payload);
  }

  /**
   * Public: whether the client can actually deliver a message to the iframe —
   * i.e. an iframe is registered AND the handshake has completed. The iframe's
   * bridge drops any non-HANDSHAKE message received before its own handshake,
   * so callers that fire one-shot messages (e.g. the handoff auto-add) should
   * wait for this before sending.
   */
  isReady(): boolean {
    return !!this.iframe?.contentWindow && this.isConnected;
  }

  /**
   * Clean up resources
   */
  destroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('message', this.handleMessage);
    }

    // Clear all pending requests
    this.pendingRequests.forEach((pending) => {
      clearTimeout(pending.timeout);
      pending.reject(new Error('Client destroyed'));
    });

    this.pendingRequests.clear();
    this.eventHandlers.clear();
    this.iframe = null;
    this.isConnected = false;
  }
}
